import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, DollarSign, Eye, MousePointer, Users, Target,
  Loader2, RefreshCw, Plus, ArrowUpRight, ArrowDownRight, Minus, Settings,
  PieChart, Activity, Megaphone, Calendar, Filter
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import api from '../api';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const KPI_CATEGORIES = [
  { value: 'engagement', label: 'Engagement' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'awareness', label: 'Awareness' },
  { value: 'custom', label: 'Custom' }
];

export const MarketingReportingPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState('monthly');
  const [campaigns, setCampaigns] = useState([]);
  const [customKPIs, setCustomKPIs] = useState([]);
  const [showKPIDialog, setShowKPIDialog] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  const [newKPI, setNewKPI] = useState({
    name: '',
    description: '',
    unit: '',
    category: 'custom',
    is_percentage: false,
    is_currency: false,
    target_higher_is_better: true
  });
  
  const [metricsForm, setMetricsForm] = useState({
    impressions: 0,
    clicks: 0,
    conversions: 0,
    leads: 0,
    spend: 0,
    revenue: 0,
    custom_kpis: []
  });

  useEffect(() => {
    fetchData();
  }, [trendPeriod]);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [overviewRes, trendsRes, campaignsRes, kpisRes] = await Promise.all([
        api.get('/marketing/reporting/overview'),
        api.get('/marketing/reporting/trends', { params: { period: trendPeriod } }),
        api.get('/marketing/campaigns'),
        api.get('/marketing/kpis')
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data.trends || []);
      setCampaigns(campaignsRes.data || []);
      setCustomKPIs(kpisRes.data || []);
    } catch (error) {
      console.error('Failed to fetch reporting data:', error);
      toast.error('Failed to load reporting data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreateKPI = async () => {
    if (!newKPI.name) {
      toast.error('KPI name is required');
      return;
    }
    
    try {
      await api.post('/marketing/kpis', newKPI);
      toast.success('Custom KPI created');
      setShowKPIDialog(false);
      setNewKPI({
        name: '',
        description: '',
        unit: '',
        category: 'custom',
        is_percentage: false,
        is_currency: false,
        target_higher_is_better: true
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create KPI');
    }
  };

  const handleUpdateMetrics = async () => {
    if (!selectedCampaign) return;
    
    try {
      await api.put(`/marketing/campaigns/${selectedCampaign.id}/metrics`, metricsForm);
      toast.success('Metrics updated');
      setShowMetricsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update metrics');
    }
  };

  const openMetricsDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setMetricsForm({
      impressions: campaign.impressions || 0,
      clicks: campaign.clicks || 0,
      conversions: campaign.conversions || 0,
      leads: campaign.leads || 0,
      spend: campaign.spend || 0,
      revenue: campaign.revenue || 0,
      custom_kpis: campaign.custom_kpis || []
    });
    setShowMetricsDialog(true);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  const getChangeIndicator = (value, isPositiveGood = true) => {
    if (value > 0) {
      return isPositiveGood ? (
        <span className="flex items-center text-green-600 text-xs">
          <ArrowUpRight className="h-3 w-3" />
          {value.toFixed(1)}%
        </span>
      ) : (
        <span className="flex items-center text-red-600 text-xs">
          <ArrowUpRight className="h-3 w-3" />
          {value.toFixed(1)}%
        </span>
      );
    } else if (value < 0) {
      return isPositiveGood ? (
        <span className="flex items-center text-red-600 text-xs">
          <ArrowDownRight className="h-3 w-3" />
          {Math.abs(value).toFixed(1)}%
        </span>
      ) : (
        <span className="flex items-center text-green-600 text-xs">
          <ArrowDownRight className="h-3 w-3" />
          {Math.abs(value).toFixed(1)}%
        </span>
      );
    }
    return <span className="flex items-center text-slate-500 text-xs"><Minus className="h-3 w-3" />0%</span>;
  };

  // Prepare chart data
  const statusChartData = overview ? Object.entries(overview.status_breakdown || {}).map(([status, count]) => ({
    name: status.replace('_', ' '),
    value: count
  })) : [];

  const channelChartData = overview ? Object.entries(overview.channel_metrics || {}).map(([channel, data]) => ({
    name: channel,
    spend: data.spend,
    conversions: data.conversions,
    campaigns: data.campaigns
  })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="marketing-reporting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Marketing Analytics
          </h1>
          <p className="text-muted-foreground">
            Track campaign performance and ROI
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchData} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button variant="outline" onClick={() => setShowKPIDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Custom KPI
            </Button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Megaphone className="h-8 w-8 text-blue-500" />
              <Badge variant="secondary">{overview?.summary?.total_campaigns || 0}</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{overview?.summary?.total_campaigns || 0}</p>
            <p className="text-xs text-muted-foreground">Total Campaigns</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{formatCurrency(overview?.summary?.total_spend)}</p>
            <p className="text-xs text-muted-foreground">Total Spend</p>
            <Progress 
              value={overview?.summary?.budget_utilization || 0} 
              className="h-1 mt-2" 
            />
            <p className="text-xs text-muted-foreground mt-1">
              {(overview?.summary?.budget_utilization || 0).toFixed(0)}% of budget
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Eye className="h-8 w-8 text-purple-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{formatNumber(overview?.summary?.total_impressions)}</p>
            <p className="text-xs text-muted-foreground">Impressions</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <MousePointer className="h-8 w-8 text-cyan-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{formatNumber(overview?.summary?.total_clicks)}</p>
            <p className="text-xs text-muted-foreground">Clicks</p>
            <p className="text-xs text-green-600 mt-1">{overview?.summary?.overall_ctr || 0}% CTR</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Target className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{formatNumber(overview?.summary?.total_conversions)}</p>
            <p className="text-xs text-muted-foreground">Conversions</p>
            <p className="text-xs text-green-600 mt-1">{overview?.summary?.overall_conversion_rate || 0}% Rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{overview?.summary?.overall_roas?.toFixed(1) || 0}x</p>
            <p className="text-xs text-muted-foreground">ROAS</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(overview?.summary?.total_revenue)} revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trends */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Performance Trends
                </CardTitle>
                <CardDescription>Campaign metrics over time</CardDescription>
              </div>
              <Select value={trendPeriod} onValueChange={setTrendPeriod}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="impressions" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Impressions" />
                <Area type="monotone" dataKey="clicks" stackId="2" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} name="Clicks" />
                <Area type="monotone" dataKey="conversions" stackId="3" stroke="#10b981" fill="#10b981" fillOpacity={0.7} name="Conversions" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Channel Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Channel Performance
            </CardTitle>
            <CardDescription>Spend and conversions by channel</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={channelChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} />
                <Tooltip formatter={(value, name) => [name === 'spend' ? formatCurrency(value) : value, name]} />
                <Legend />
                <Bar dataKey="spend" fill="#3b82f6" name="Spend ($)" />
                <Bar dataKey="conversions" fill="#10b981" name="Conversions" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Status & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <RechartsPie>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top by Conversions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-500" />
              Top by Conversions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(overview?.top_by_conversions || []).map((campaign, idx) => (
                <div key={campaign.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                      {idx + 1}
                    </Badge>
                    <Link to={`/marketing/campaigns/${campaign.id}`} className="text-sm hover:underline">
                      {campaign.name}
                    </Link>
                  </div>
                  <Badge className="bg-green-100 text-green-700">{campaign.conversions}</Badge>
                </div>
              ))}
              {(!overview?.top_by_conversions || overview.top_by_conversions.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top by ROI */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Top by ROI
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(overview?.top_by_roi || []).map((campaign, idx) => (
                <div key={campaign.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                      {idx + 1}
                    </Badge>
                    <Link to={`/marketing/campaigns/${campaign.id}`} className="text-sm hover:underline">
                      {campaign.name}
                    </Link>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">{campaign.roi}%</Badge>
                </div>
              ))}
              {(!overview?.top_by_roi || overview.top_by_roi.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Metrics Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Campaign Metrics</CardTitle>
              <CardDescription>Click on a campaign to update its metrics</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="pb-3 font-medium">Campaign</th>
                  <th className="pb-3 font-medium text-right">Impressions</th>
                  <th className="pb-3 font-medium text-right">Clicks</th>
                  <th className="pb-3 font-medium text-right">CTR</th>
                  <th className="pb-3 font-medium text-right">Conversions</th>
                  <th className="pb-3 font-medium text-right">Conv. Rate</th>
                  <th className="pb-3 font-medium text-right">Spend</th>
                  <th className="pb-3 font-medium text-right">Revenue</th>
                  <th className="pb-3 font-medium text-right">ROI</th>
                  <th className="pb-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-b hover:bg-slate-50">
                    <td className="py-3">
                      <Link to={`/marketing/campaigns/${campaign.id}`} className="font-medium hover:underline">
                        {campaign.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">{campaign.status}</p>
                    </td>
                    <td className="py-3 text-right">{formatNumber(campaign.impressions)}</td>
                    <td className="py-3 text-right">{formatNumber(campaign.clicks)}</td>
                    <td className="py-3 text-right">{campaign.ctr?.toFixed(1) || 0}%</td>
                    <td className="py-3 text-right">{formatNumber(campaign.conversions)}</td>
                    <td className="py-3 text-right">{campaign.conversion_rate?.toFixed(1) || 0}%</td>
                    <td className="py-3 text-right">{formatCurrency(campaign.spend)}</td>
                    <td className="py-3 text-right">{formatCurrency(campaign.revenue)}</td>
                    <td className="py-3 text-right">
                      <Badge className={campaign.roi > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {campaign.roi?.toFixed(0) || 0}%
                      </Badge>
                    </td>
                    <td className="py-3 text-right">
                      {hasRole(['admin', 'marketing_manager']) && (
                        <Button variant="ghost" size="sm" onClick={() => openMetricsDialog(campaign)}>
                          <Settings className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Custom KPIs */}
      {customKPIs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Custom KPIs</CardTitle>
            <CardDescription>Your custom tracking metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {customKPIs.map((kpi) => (
                <Badge key={kpi.id} variant="outline" className="px-3 py-1">
                  {kpi.name}
                  {kpi.unit && <span className="ml-1 text-muted-foreground">({kpi.unit})</span>}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Custom KPI Dialog */}
      <Dialog open={showKPIDialog} onOpenChange={setShowKPIDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom KPI</DialogTitle>
            <DialogDescription>Create a new KPI to track across campaigns</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>KPI Name *</Label>
              <Input
                placeholder="e.g., Email Open Rate, Social Shares"
                value={newKPI.name}
                onChange={(e) => setNewKPI({ ...newKPI, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="What does this KPI measure?"
                value={newKPI.description}
                onChange={(e) => setNewKPI({ ...newKPI, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  placeholder="%, $, #"
                  value={newKPI.unit}
                  onChange={(e) => setNewKPI({ ...newKPI, unit: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newKPI.category} onValueChange={(v) => setNewKPI({ ...newKPI, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KPI_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKPIDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateKPI}>Create KPI</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Metrics Dialog */}
      <Dialog open={showMetricsDialog} onOpenChange={setShowMetricsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Campaign Metrics</DialogTitle>
            <DialogDescription>{selectedCampaign?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Impressions</Label>
                <Input
                  type="number"
                  value={metricsForm.impressions}
                  onChange={(e) => setMetricsForm({ ...metricsForm, impressions: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Clicks</Label>
                <Input
                  type="number"
                  value={metricsForm.clicks}
                  onChange={(e) => setMetricsForm({ ...metricsForm, clicks: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Conversions</Label>
                <Input
                  type="number"
                  value={metricsForm.conversions}
                  onChange={(e) => setMetricsForm({ ...metricsForm, conversions: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Leads</Label>
                <Input
                  type="number"
                  value={metricsForm.leads}
                  onChange={(e) => setMetricsForm({ ...metricsForm, leads: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Spend ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={metricsForm.spend}
                  onChange={(e) => setMetricsForm({ ...metricsForm, spend: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Revenue ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={metricsForm.revenue}
                  onChange={(e) => setMetricsForm({ ...metricsForm, revenue: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            {/* Calculated metrics preview */}
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">
                  {metricsForm.impressions > 0 ? ((metricsForm.clicks / metricsForm.impressions) * 100).toFixed(2) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">CTR</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-600">
                  {metricsForm.clicks > 0 ? ((metricsForm.conversions / metricsForm.clicks) * 100).toFixed(2) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">Conv. Rate</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-purple-600">
                  ${metricsForm.clicks > 0 ? (metricsForm.spend / metricsForm.clicks).toFixed(2) : 0}
                </p>
                <p className="text-xs text-muted-foreground">CPC</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-600">
                  {metricsForm.spend > 0 ? (((metricsForm.revenue - metricsForm.spend) / metricsForm.spend) * 100).toFixed(0) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">ROI</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetricsDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateMetrics}>Save Metrics</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingReportingPage;
